import React, { useState, useEffect, Suspense, lazy } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Eye, Heart, User, Star, ArrowRight, CheckCircle, Users, Briefcase, Award, Zap, Search, Mic, Camera } from 'lucide-react';
import FAQ from '../components/FAQ';
import AnimatedCounter from '../components/AnimatedCounter';
import SEO from '../components/SEO';
import WhyChooseUs from '../components/WhyChooseUs';
import TrustSignals from '../components/TrustSignals';
import CTABanner from '../components/CTABanner';
import HeroCarousel from '../components/HeroCarousel';

import CompanyStats from '../components/CompanyStats';
import ProfessionalServices from '../components/ProfessionalServices';
import CertificationsAndPartnerships from '../components/CertificationsAndPartnerships';
import TechStackCarousel from '../components/TechStackCarousel';
import heroWorkstationImage from '../assets/hero/hero-workstation.png';
import { combineKeywords } from '../utils/seoConfig';
import { preloadAllSections, areSectionsPreloaded, forceRenderSections } from '../utils/preloader';
import { enablePerformanceOptimizations, preloadCriticalResources, enableHardwareAcceleration, optimizeScrollPerformance } from '../utils/performanceOptimization';

const Hero = styled.section`
  text-align: center;
  padding: 60px 10px;
  color: white;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  overflow: hidden;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 20px;
  background: linear-gradient(135deg, #fff 0%, #f0f0f0 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.p)`
  font-size: 1.3rem;
  margin-bottom: 40px;
  opacity: 0.9;
  line-height: 1.6;
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-bottom: 60px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAButton = styled.button`
  padding: 15px 30px;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &.primary {
    background: white;
    color: #667eea;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  }
  
  &.secondary {
    background: transparent;
    color: white;
    border: 2px solid white;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(0,0,0,0.3);
  }
`;

const Stats = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 40px;
  margin-top: 60px;
`;

const StatItem = styled.div`
  text-align: center;
  
  h3 {
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 5px;
  }
  
  p {
    opacity: 0.8;
    font-size: 1.1rem;
  }
`;

const Section = styled.section`
  padding: 60px 0;
  margin: 0;
  background: 
    radial-gradient(ellipse 1200px 600px at 0% 100%, rgba(59, 130, 246, 0.9) 0%, rgba(59, 130, 246, 0.4) 40%, transparent 70%),
    radial-gradient(ellipse 1000px 500px at 100% 0%, rgba(236, 72, 153, 0.8) 0%, rgba(168, 85, 247, 0.5) 30%, transparent 60%),
    linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  width: 100%;
  overflow-x: hidden;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 200px;
    background: 
      linear-gradient(45deg, 
        transparent 0%, 
        rgba(59, 130, 246, 0.5) 20%, 
        rgba(99, 102, 241, 0.7) 35%, 
        rgba(139, 92, 246, 0.8) 50%, 
        rgba(168, 85, 247, 0.7) 65%, 
        rgba(236, 72, 153, 0.5) 80%, 
        transparent 95%
      );
    clip-path: 
      polygon(
        0 100%, 0 80%, 5% 78%, 10% 82%, 15% 76%, 20% 80%, 25% 74%, 30% 78%, 35% 72%, 40% 76%, 45% 70%, 
        50% 74%, 55% 68%, 60% 72%, 65% 66%, 70% 70%, 75% 64%, 80% 68%, 85% 62%, 90% 66%, 95% 60%, 100% 64%, 100% 100%
      );
  }
  
  > * {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 20px;
    position: relative;
    z-index: 2;
    
    @media (max-width: 768px) {
      padding: 0 15px;
    }
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: linear-gradient(90deg, #00ff41, #00ffff, #ff0080);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 15px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.4rem;
    margin-bottom: 12px;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.2rem;
  color: #ffffff;
  margin-bottom: 60px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 40px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    margin-bottom: 30px;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 60px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    margin-bottom: 40px;
  }
`;

const FeatureCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 40px 30px;
  border-radius: 25px;
  text-align: center;
  transition: none;
  position: relative;
  overflow: hidden;
  transform: translateZ(0);
  will-change: transform, opacity;
  
  @media (max-width: 768px) {
    padding: 30px 20px;
    backdrop-filter: none;
    background: rgba(135,206,235,0.15);
  }
  
  @media (min-width: 769px) {
    &:hover {
      transform: translateY(-8px) translateZ(0);
      border-color: rgba(135,206,235,0.5);
      box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    }
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: white;
`;

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 25px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin-bottom: 60px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    margin-bottom: 40px;
  }
`;

const ProjectCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 25px;
  overflow: hidden;
  transition: none;
  position: relative;
  will-change: transform, opacity;

  @media (min-width: 769px) {
    &:hover {
      transform: translateY(-8px);
      border-color: rgba(135,206,235,0.5);
      box-shadow: 0 8px 25px rgba(0,0,0,0.15);
    }
  }
`;

const ProjectImage = styled.div`
  height: 200px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ProjectContent = styled.div`
  padding: 25px;
`;

const ProjectTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 10px;
  color: #87ceeb;
`;

const ProjectMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  color: #ffffff;
`;

const TestimonialSection = styled.section`
  background: 
    radial-gradient(ellipse 1200px 600px at 0% 100%, rgba(59, 130, 246, 0.9) 0%, rgba(59, 130, 246, 0.4) 40%, transparent 70%),
    radial-gradient(ellipse 1000px 500px at 100% 0%, rgba(236, 72, 153, 0.8) 0%, rgba(168, 85, 247, 0.5) 30%, transparent 60%),
    linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  padding: 60px 0;
  margin: 0;
  width: 100%;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: 
      radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 200px;
    background: 
      linear-gradient(45deg, 
        transparent 0%, 
        rgba(59, 130, 246, 0.5) 20%, 
        rgba(99, 102, 241, 0.7) 35%, 
        rgba(139, 92, 246, 0.8) 50%, 
        rgba(168, 85, 247, 0.7) 65%, 
        rgba(236, 72, 153, 0.5) 80%, 
        transparent 95%
      );
    clip-path: 
      polygon(
        0 100%, 0 80%, 5% 78%, 10% 82%, 15% 76%, 20% 80%, 25% 74%, 30% 78%, 35% 72%, 40% 76%, 45% 70%, 
        50% 74%, 55% 68%, 60% 72%, 65% 66%, 70% 70%, 75% 64%, 80% 68%, 85% 62%, 90% 66%, 95% 60%, 100% 64%, 100% 100%
      );
  }
  
  > * {
    max-width: 1200px;
    margin-left: auto;
    margin-right: auto;
    padding: 0 10px;
    position: relative;
    z-index: 2;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
  }
`;

const TestimonialCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 35px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  transition: none;
  will-change: transform, opacity;
  
  @media (min-width: 769px) {
    &:hover {
      transform: translateY(-5px);
      border-color: rgba(135,206,235,0.5);
      box-shadow: 0 8px 20px rgba(0,0,0,0.1);
    }
  }
`;

const TestimonialText = styled.p`
  font-style: italic;
  margin-bottom: 20px;
  color: #ffffff;
  line-height: 1.6;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const AuthorAvatar = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0a0e27;
  position: relative;
  overflow: hidden;
  padding-top: 20px;
  width: 100%;
  isolation: isolate;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${heroWorkstationImage});
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    transform: scale(1.04);
    filter: blur(5px) sepia(0.12) saturate(1.08) hue-rotate(-10deg) brightness(0.98);
    z-index: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      linear-gradient(135deg, rgba(16, 14, 28, 0.74) 0%, rgba(26, 22, 44, 0.66) 45%, rgba(33, 24, 52, 0.72) 100%),
      radial-gradient(ellipse at 18% 28%, rgba(245, 158, 11, 0.14) 0%, transparent 52%),
      radial-gradient(ellipse at 80% 70%, rgba(96, 165, 250, 0.12) 0%, transparent 50%);
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    padding-top: 30px;
    padding-bottom: 30px;
    min-height: 90vh;

    &::before {
      transform: scale(1.03);
      filter: blur(3px) sepia(0.1) saturate(1.06) hue-rotate(-8deg) brightness(0.98);
    }
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
    padding: 0 15px;
  }
  
  @media (max-width: 768px) {
    gap: 30px;
    padding: 0 20px;
  }
  
  @media (max-width: 480px) {
    gap: 20px;
    padding: 0 15px;
  }
`;

const HeroHeading = styled.h1`
  font-size: clamp(2.2rem, 4.5vw, 3.5rem);
  font-weight: 900;
  margin-bottom: 15px;
  line-height: 1.1;
  color: #e6f1ff;
  text-shadow: 0 6px 22px rgba(14, 165, 233, 0.22);

  @supports ((-webkit-background-clip: text) or (background-clip: text)) {
    background: linear-gradient(90deg, #e6f1ff 0%, #8fd3ff 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  @media (max-width: 768px) {
    font-size: clamp(1.95rem, 8vw, 2.55rem);
    line-height: 1.14;
  }

  @media (max-width: 420px) {
    font-size: clamp(1.75rem, 8.4vw, 2.2rem);
  }
`;

const HeroTagline = styled.div`
  font-size: clamp(1.2rem, 2.5vw, 1.6rem);
  font-weight: 600;
  margin-bottom: 20px;
  color: #7dd3fc;
  font-style: italic;
`;

const HeroParagraph = styled.p`
  font-size: clamp(1rem, 2vw, 1.2rem);
  font-weight: 400;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
`;

const HeroParagraphSecondary = styled.p`
  font-size: clamp(0.9rem, 1.7vw, 1rem);
  margin-bottom: 25px;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  font-weight: 400;
`;

const HeroActions = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  justify-content: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }
`;

const HeroChecklist = styled.div`
  text-align: center;
  font-size: clamp(0.85rem, 1.5vw, 0.95rem);
  color: rgba(255, 255, 255, 0.75);
  font-weight: 500;
  margin-bottom: 20px;
  line-height: 1.6;
`;

const HeroStatsCard = styled.div`
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(125, 211, 252, 0.22);
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  box-shadow: 0 10px 26px rgba(2, 6, 23, 0.25);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const HeroStatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: clamp(10px, 3vw, 20px);
  font-size: clamp(0.8rem, 1.4vw, 1.1rem);
  font-weight: 600;
  flex-wrap: nowrap;
  white-space: nowrap;

  @media (max-width: 560px) {
    gap: 10px;
    font-size: 0.98rem;
  }
`;

const HeroStat = styled.span`
  color: rgba(255, 255, 255, 0.75);

  strong {
    color: #7dd3fc;
    font-weight: 700;
  }
`;

const HeroStatSeparator = styled.span`
  color: rgba(255, 255, 255, 0.5);
`;

const SearchTitle = styled.h1`
  font-size: 4rem;
  margin-bottom: 25px;
  font-weight: 900;
  font-family: 'Product Sans', Arial, sans-serif;
  position: relative;
  letter-spacing: 3px;
  
  .in { 
    color: #ff9933;
    text-shadow: 0 0 15px rgba(255,153,51,0.5);
    animation: saffronNetwork 2s ease-in-out infinite alternate;
    font-weight: 900;
    display: inline-block;
  }
  .it { 
    color: #ffffff;
    text-shadow: 0 0 15px rgba(255,255,255,0.7);
    animation: whiteNetwork 2s ease-in-out infinite alternate 0.5s;
    font-weight: 900;
    display: inline-block;
  }
  .x { 
    color: #138808;
    text-shadow: 0 0 15px rgba(19,136,8,0.5);
    animation: greenNetwork 2s ease-in-out infinite alternate 1s;
    font-weight: 900;
    display: inline-block;
  }
  
  @keyframes saffronNetwork {
    0% { 
      text-shadow: 0 0 15px rgba(255,153,51,0.5);
      transform: scale(1);
    }
    100% { 
      text-shadow: 0 0 25px rgba(255,153,51,0.7);
      transform: scale(1.02);
    }
  }
  
  @keyframes whiteNetwork {
    0% { 
      text-shadow: 0 0 15px rgba(255,255,255,0.7);
      transform: scale(1);
    }
    100% { 
      text-shadow: 0 0 25px rgba(255,255,255,0.9);
      transform: scale(1.02);
    }
  }
  
  @keyframes greenNetwork {
    0% { 
      text-shadow: 0 0 15px rgba(19,136,8,0.5);
      transform: scale(1);
    }
    100% { 
      text-shadow: 0 0 25px rgba(19,136,8,0.7);
      transform: scale(1.02);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 2.8rem;
    letter-spacing: 2px;
  }
  
  @media (max-width: 480px) {
    font-size: 2.2rem;
    letter-spacing: 1px;
  }
`;

const SearchSubtitle = styled.p`
  display: none;
`;

const SearchBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(10px);
  border: 2px solid transparent;
  border-radius: 25px;
  padding: 0;
  transition: all 0.3s ease;
  max-width: 100%;
  margin: 0 auto 25px;
  box-shadow: 0 6px 24px rgba(0,0,0,0.1);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, 
      #ff9933 0%, 
      #ffffff 25%, 
      #138808 50%, 
      #ff9933 75%, 
      #ffffff 100%);
    border-radius: 27px;
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 32px rgba(0,0,0,0.15);
      
    &::before {
      opacity: 1;
    }
  }
  
  &:focus-within {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 10px 32px rgba(0,0,0,0.2);
      
    &::before {
      opacity: 1;
    }
  }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  padding: 10px 14px;
  font-size: 15px;
  background: transparent;
  color: #3c4043;
  
  &::placeholder {
    color: #9aa0a6;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 8px 12px;
  }
`;

const SearchIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 12px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(66, 133, 244, 0.1);
  }
  
  &.camera {
    color: #ea4335;
    
    &:hover {
      background: rgba(234, 67, 53, 0.1);
    }
  }
  
  &.mic {
    color: #34a853;
    
    &:hover {
      background: rgba(52, 168, 83, 0.1);
    }
  }
`;

const SearchButton = styled.button`
  background: ${({ $variant }) =>
    $variant === 'secondary'
      ? 'rgba(255,255,255,0.08)'
      : 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 55%, #1d4ed8 100%)'};
  border: ${({ $variant }) =>
    $variant === 'secondary'
      ? '1px solid rgba(255,255,255,0.25)'
      : '1px solid rgba(255,255,255,0.22)'};
  border-radius: 25px;
  padding: 16px 32px;
  color: ${({ $variant }) => ($variant === 'secondary' ? '#d1e9ff' : '#ffffff')};
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  margin: 6px;
  transition: all 0.3s ease;
  text-shadow: 0 1px 4px rgba(2, 6, 23, 0.35);
  position: relative;
  overflow: hidden;
  box-shadow: ${({ $variant }) =>
    $variant === 'secondary'
      ? '0 8px 20px rgba(2, 6, 23, 0.24)'
      : '0 10px 24px rgba(2, 6, 23, 0.34)'};
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: ${({ $variant }) =>
      $variant === 'secondary'
        ? '0 12px 26px rgba(2, 6, 23, 0.32)'
        : '0 14px 30px rgba(2, 6, 23, 0.42)'};
    color: ${({ $variant }) => ($variant === 'secondary' ? '#e6f3ff' : '#ffffff')};
    text-shadow: 0 1px 5px rgba(2, 6, 23, 0.45);
    background: ${({ $variant }) =>
      $variant === 'secondary'
        ? 'rgba(147, 197, 253, 0.18)'
        : 'linear-gradient(135deg, #0ea5e9 0%, #1d4ed8 100%)'};
    border-color: ${({ $variant }) =>
      $variant === 'secondary'
        ? 'rgba(255,255,255,0.35)'
        : 'rgba(255,255,255,0.3)'};
    
    &::before {
      left: 100%;
    }
  }
  
  @media (max-width: 480px) {
    width: 100%;
    max-width: 320px;
    margin: 0 auto;
    padding: 13px 22px;
    font-size: 15px;
  }
`;

const SearchButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 25px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  @media (max-width: 480px) {
    gap: 8px;
  }
`;

const QuickLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
  
  @media (max-width: 480px) {
    gap: 10px;
  }
`;

const QuickLink = styled.a`
  color: #87ceeb;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 20px;
  transition: all 0.3s ease;
  position: relative;
  background: rgba(255,255,255,0.05);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255,255,255,0.1);
  
  &:hover {
    color: #fff;
    background: rgba(255,255,255,0.1);
    transform: translateY(-2px) scale(1.03);
    text-shadow: 0 0 12px rgba(255,255,255,0.8);
    box-shadow: 0 6px 20px rgba(0,0,0,0.2);
  }
  
  @media (max-width: 480px) {
    font-size: 13px;
    padding: 6px 12px;
  }
`;



const Home = () => {
  const [projects, setProjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent?.toLowerCase().includes('reactsnap');
  const [mockStats] = useState(() => ({
    views: Math.floor(Math.random() * 100) + 50,
    likes: Math.floor(Math.random() * 50) + 20
  }));

  // Preload all sections immediately
  useEffect(() => {
    const initializePreloading = async () => {
      // Enable all performance optimizations
      enablePerformanceOptimizations();
      preloadCriticalResources();
      enableHardwareAcceleration();
      optimizeScrollPerformance();
      
      // Check if already preloaded
      if (!areSectionsPreloaded()) {
        // Force immediate rendering of all components
        forceRenderSections();
        
        // Preload all assets and sections
        await preloadAllSections();
      }
      
      setSectionsLoaded(true);
    };
    
    // Start preloading immediately
    initializePreloading();
  }, []);

  const seoData = {
    title: 'InitX Technologies | Custom Software Development & IT Consulting',
    description: 'InitX Technologies provides expert custom software development, mobile apps, and IT consulting for startups and enterprises. Build scalable, high-performance digital products today.',
    keywords: combineKeywords([
      'InitX Technologies', 'InitX', 'web development', 'custom software solutions', 'AI integration', 'digital transformation',
      'scalable digital products', 'modern technology brand', 'startup technology solutions', 'business automation',
      'React development', 'Node.js', 'MongoDB', 'cloud systems', 'UI/UX design', 'mobile applications',
      'technology solutions', 'AI development', 'artificial intelligence', 'machine learning', 'deep learning', 'neural networks',
      'computer vision', 'natural language processing', 'speech recognition', 'image recognition', 'predictive modeling',
      'cloud computing', 'cloud infrastructure', 'AWS services', 'Azure solutions', 'Google Cloud Platform', 'cloud migration',
      'serverless computing', 'edge computing', 'hybrid cloud', 'multi-cloud', 'cloud security', 'cloud optimization',
      'web development', 'React development', 'Vue.js', 'Angular', 'Node.js development', 'Express.js', 'Next.js',
      'Python development', 'Django', 'Flask', 'FastAPI', 'Java development', 'Spring Boot', 'PHP development', 'Laravel',
      'mobile app development', 'iOS development', 'Swift', 'Android development', 'Kotlin', 'React Native', 'Flutter development',
      'Xamarin', 'Ionic', 'cross-platform apps', 'native mobile apps', 'progressive web apps', 'hybrid apps',
      'digital transformation', 'enterprise solutions', 'startup technology', 'tech consulting', 'software engineering',
      'custom software development', 'software architecture', 'system design', 'technical consulting', 'IT strategy',
      'DevOps services', 'CI/CD pipeline', 'continuous integration', 'continuous deployment', 'containerization',
      'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI', 'GitHub Actions', 'microservices architecture', 'serverless architecture',
      'cybersecurity solutions', 'penetration testing', 'security audit', 'vulnerability assessment', 'GDPR compliance',
      'data protection', 'encryption', 'firewall', 'intrusion detection', 'security monitoring', 'threat intelligence',
      'data analytics', 'big data', 'business intelligence', 'data science', 'predictive analytics', 'data visualization',
      'machine learning models', 'statistical analysis', 'data mining', 'ETL processes', 'data warehousing',
      'API development', 'REST API', 'GraphQL', 'SOAP services', 'webhook development', 'third-party integrations',
      'microservices', 'API gateway', 'API security', 'API documentation', 'API testing', 'API monitoring',
      'database design', 'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Elasticsearch', 'Cassandra', 'DynamoDB',
      'data modeling', 'database optimization', 'database migration', 'NoSQL', 'SQL', 'data architecture',
      'blockchain development', 'cryptocurrency', 'smart contracts', 'DeFi solutions', 'NFT development', 'Web3',
      'Ethereum', 'Solidity', 'Bitcoin', 'crypto wallet', 'decentralized applications', 'consensus algorithms',
      'IoT solutions', 'Internet of Things', 'embedded systems', 'sensor networks', 'industrial IoT', 'smart devices',
      'MQTT', 'LoRaWAN', 'edge analytics', 'IoT security', 'device management', 'real-time monitoring',
      'automation solutions', 'RPA', 'robotic process automation', 'workflow automation', 'business process automation',
      'chatbots', 'virtual assistants', 'process optimization', 'digital workflows', 'task automation',
      'tech innovation', 'emerging technologies', 'digital solutions', 'technology consulting', 'IT services',
      'managed services', 'cloud consulting', 'digital strategy', 'technology roadmap', 'innovation consulting',
      'software development company', 'technology partner', 'digital agency', 'innovation lab', 'tech startup',
      'enterprise software', 'B2B solutions', 'B2C applications', 'white-label solutions', 'custom integrations',
      'fintech solutions', 'payment processing', 'digital banking', 'insurtech', 'regtech', 'wealthtech',
      'healthtech', 'telemedicine', 'health informatics', 'medical software', 'HIPAA compliance', 'EHR systems',
      'edtech', 'e-learning platforms', 'LMS', 'educational software', 'online training', 'virtual classrooms',
      'e-commerce development', 'marketplace development', 'shopping cart', 'payment gateway', 'inventory management',
      'SaaS development', 'PaaS solutions', 'IaaS', 'multi-tenant architecture', 'subscription billing',
      'agile development', 'scrum methodology', 'kanban', 'lean startup', 'MVP development', 'prototype development',
      'UI/UX design', 'user experience', 'user interface', 'responsive design', 'mobile-first design', 'accessibility',
      'performance optimization', 'scalability', 'high availability', 'load balancing', 'system architecture',
      'quality assurance', 'software testing', 'automated testing', 'test-driven development', 'code review',
      'technical support', 'maintenance', 'bug fixes', 'feature updates', 'system monitoring', 'performance tuning'
    ])
  };

  useEffect(() => {
    if (isPrerender) {
      return;
    }
    fetchProjects();
  }, [isPrerender]);

  const fetchProjects = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const isProduction = process.env.NODE_ENV === 'production';

    if (isProduction && !apiUrl) {
      return;
    }

    const baseUrl = apiUrl || 'http://localhost:5001';

    try {
      const response = await axios.get(`${baseUrl}/api/projects/public`);
      setProjects(response.data);
    } catch (error) {
      if (!isProduction) {
        console.error('Error fetching projects:', error);
      }
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      // TODO: Implement actual search functionality
      console.log(`Searching for: ${searchQuery}`);
    }
  };

  const handleCameraSearch = () => {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ video: true })
        .then(stream => {
          // Create video element for camera preview
          const video = document.createElement('video');
          video.srcObject = stream;
          video.play();
          
          // For demo - in real app you'd implement image recognition
          setTimeout(() => {
            stream.getTracks().forEach(track => track.stop());
            alert('Camera search feature - would analyze image and search');
          }, 3000);
        })
        .catch(err => {
          alert('Camera access denied or not available');
        });
    } else {
      alert('Camera not supported in this browser');
    }
  };

  const handleVoiceSearch = () => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
      };
      
      recognition.start();
    } else {
      alert('Voice search not supported in this browser');
    }
  };

  const handleQuickSearch = (query) => {
    setSearchQuery(query);
    // TODO: Implement actual search functionality
    console.log(`Quick search: ${query}`);
  };



  const services = [
    {
      icon: <Briefcase size={40} />,
      title: "Web Development",
      description: "Custom web applications built with React, Node.js, and modern frameworks. Responsive, fast, and SEO-optimized solutions for your business needs."
    },
    {
      icon: <Users size={40} />,
      title: "Mobile App Development",
      description: "Native iOS and Android apps, React Native, and Flutter development. Cross-platform solutions that deliver exceptional user experiences."
    },
    {
      icon: <Zap size={40} />,
      title: "AI & Machine Learning",
      description: "Artificial intelligence solutions, machine learning models, and data analytics to automate processes and drive intelligent decision-making."
    },
    {
      icon: <Award size={40} />,
      title: "Cloud Solutions",
      description: "AWS, Azure, and Google Cloud services. Cloud migration, serverless architecture, and scalable infrastructure for modern applications."
    },
    {
      icon: <CheckCircle size={40} />,
      title: "Digital Transformation",
      description: "Complete digital transformation services including process automation, system integration, and technology consulting for enterprises."
    }
  ];

  const testimonials = [
    {
      text: "InitX Technologies transformed our entire digital infrastructure. Their AI-powered analytics platform increased our operational efficiency by 45% and reduced costs by $2M annually. The team's expertise in cloud architecture and machine learning is exceptional.",
      author: "Sarah Johnson",
      role: "CTO, TechCorp Solutions",
      company: "Fortune 500 Financial Services"
    },
    {
      text: "Working with InitX was a game-changer for our healthcare platform. They delivered a HIPAA-compliant telemedicine solution that now serves over 100,000 patients across 15 countries. Their attention to security and scalability is unmatched.",
      author: "Dr. Michael Chen",
      role: "CEO, HealthPlus Medical",
      company: "Leading Healthcare Provider"
    },
    {
      text: "InitX's AI-powered learning platform revolutionized our education delivery. With over 1 million students now using the system, we've seen 85% completion rates and 4.8/5 user satisfaction. Their technical excellence is outstanding.",
      author: "Emma Davis",
      role: "VP Technology, EduTech Global",
      company: "Global Education Technology"
    },
    {
      text: "The omnichannel commerce platform InitX built for us unified our 200+ stores with our online presence. Sales increased by 150% and customer satisfaction improved dramatically. They truly understand enterprise-scale challenges.",
      author: "James Wilson",
      role: "Chief Digital Officer, RetailMax",
      company: "International Retail Chain"
    },
    {
      text: "InitX's cloud migration services were flawless. They moved our entire infrastructure to AWS with zero downtime and improved our application performance by 300%. Their DevOps expertise saved us millions in operational costs.",
      author: "Lisa Rodriguez",
      role: "Head of Infrastructure, CloudFirst Inc",
      company: "Enterprise Software Company"
    },
    {
      text: "The custom ERP solution InitX developed for our manufacturing operations streamlined our entire supply chain. We achieved 40% reduction in processing time and 99.9% system uptime. Exceptional project delivery.",
      author: "Robert Kumar",
      role: "COO, ManufacturePro Ltd",
      company: "Global Manufacturing Leader"
    }
  ];

  return (
    <>
      {/* SEO Components */}
<SEO 
        title="InitX Technologies | Custom Software Development & IT Consulting"
        description="InitX Technologies provides expert custom software development, mobile apps, and IT consulting for startups and enterprises. Build scalable, high-performance digital products today."
        keywords={seoData.keywords}
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[{ name: 'Home', url: '/' }]}
      />
      
      {/* 1. Hero Section */}
      <HeroSection>
        <HeroContainer>
          <div>
            <HeroHeading>
              Custom Software & Web Development Services
            </HeroHeading>
            
            <HeroTagline>
              Initiating innovation with expertise
            </HeroTagline>
            
            <HeroParagraph>
              An IT consulting and software development company helping startups, scale-ups, and enterprises build reliable web, mobile, and cloud-based products. Focus on clean architecture, scalable systems, and solutions that grow with your business.
            </HeroParagraph>
          </div>
          
          <div>
            <HeroParagraphSecondary>
              From strategy to deployment, we design and build high-performance web, mobile, and cloud solutions. Our teams partner with founders and technology leaders to transform complex ideas into reliable, scalable digital products.
            </HeroParagraphSecondary>
            
            <HeroActions>
              <SearchButton onClick={() => window.location.href = '/contact'}>Get Free Consultation</SearchButton>
              <SearchButton $variant="secondary" onClick={() => window.location.href = '/services'}>Explore Services</SearchButton>
            </HeroActions>
            
            <HeroChecklist>
              ✓ Expert-driven development • ✓ Scalable architecture design<br/>
              ✓ End-to-end project delivery
            </HeroChecklist>
            
            <HeroStatsCard>
              <HeroStatsRow>
                <HeroStat><strong>98%</strong> Success Rate</HeroStat>
                <HeroStatSeparator>•</HeroStatSeparator>
                <HeroStat><strong>30+</strong> Projects</HeroStat>
                <HeroStatSeparator>•</HeroStatSeparator>
                <HeroStat><strong>24/7</strong> Support</HeroStat>
              </HeroStatsRow>
            </HeroStatsCard>
          </div>
        </HeroContainer>
      </HeroSection>

      {/* 2. Hero Carousel */}
      <div className="container">
        <HeroCarousel />
      </div>

      {/* 3. Platform Features */}
      <Section data-section="services" style={{ willChange: 'transform', transform: 'translateZ(0)' }}>
        <SectionTitle>Explore Our Platform</SectionTitle>
        <SectionSubtitle>
          Discover all the features and services available on InitX Technologies platform
        </SectionSubtitle>

        <FeaturesGrid>
          <FeatureCard
            className="motion-div"
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.02 }}
            onClick={() => window.location.href = '/about'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <Users size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>About Us</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Learn about InitX Technologies, our mission, vision, and the expert team behind innovative technology solutions.
            </p>
          </FeatureCard>

          <FeatureCard
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.05 }}
            onClick={() => window.location.href = '/products'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <Briefcase size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>Products</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Explore our cutting-edge software products and digital solutions designed to transform your business operations.
            </p>
          </FeatureCard>

          <FeatureCard
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.08 }}
            onClick={() => window.location.href = '/blogs'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <Award size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>Blogs</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Stay updated with latest technology trends, insights, and expert articles from our development team.
            </p>
          </FeatureCard>

          <FeatureCard
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.1 }}
            onClick={() => window.location.href = '/careers'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <Zap size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>Careers</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Join our innovative team and build the future of technology. Explore exciting career opportunities with us.
            </p>
          </FeatureCard>

          <FeatureCard
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.12 }}
            onClick={() => window.location.href = '/contact'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <CheckCircle size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>Contact</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Get in touch with our team for consultations, project discussions, or any technology-related inquiries.
            </p>
          </FeatureCard>

          <FeatureCard
            initial={{ opacity: 0, y: 5 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.15, ease: "easeOut", delay: 0.15 }}
            onClick={() => window.location.href = '/quote'}
            style={{ cursor: 'pointer' }}
          >
            <FeatureIcon>
              <ArrowRight size={40} />
            </FeatureIcon>
            <h3 style={{ color: '#87ceeb', marginBottom: '15px', fontSize: '1.3rem' }}>Get Quote</h3>
            <p style={{ color: '#ffffff', lineHeight: '1.6' }}>
              Share your requirements and get a tailored project estimate from our team.
            </p>
          </FeatureCard>
        </FeaturesGrid>
      </Section>

      {/* 4. Why Choose Us */}
      <WhyChooseUs />

      {/* 5. Our Technology Stack */}
      <Section style={{ willChange: 'transform', transform: 'translateZ(0)' }}>
        <SectionTitle>Our Technology Stack</SectionTitle>
        <SectionSubtitle>
          We use cutting-edge technologies and frameworks to build robust, scalable, and modern solutions
        </SectionSubtitle>
        
        <TechStackCarousel />
      </Section>

      {/* 6. Recent Projects */}
      <Section style={{ willChange: 'transform', transform: 'translateZ(0)' }}>
        <SectionTitle>Our Recent Projects</SectionTitle>
        <SectionSubtitle>
          Real solutions we've delivered for businesses across different industries
        </SectionSubtitle>

        <ProjectGrid>
          {projects.length > 0 ? projects.slice(0, 6).map((project, index) => (
            <ProjectCard
              key={project._id}
              initial={{ opacity: 0, y: 5 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.15, ease: "easeOut", delay: Math.min(index * 0.03, 0.1) }}
            >
              <ProjectImage>
                {project.title}
              </ProjectImage>
              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <p style={{ color: '#ffffff', marginBottom: '15px' }}>{project.description}</p>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                  <span style={{ 
                    background: '#e0e7ff', 
                    color: '#3730a3', 
                    padding: '4px 8px', 
                    borderRadius: '6px', 
                    fontSize: '12px' 
                  }}>
                    {project.category}
                  </span>
                </div>
                <ProjectMeta>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <User size={16} />
                    {project.designer?.name}
                  </div>
                  <div style={{ display: 'flex', gap: '15px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Eye size={16} />
                      {mockStats.views + index * 10}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Heart size={16} />
                      {mockStats.likes + index * 5}
                    </span>
                  </div>
                </ProjectMeta>
              </ProjectContent>
            </ProjectCard>
          )) : (
            // Real project examples when no data
            [
              {
                title: "E-commerce Dashboard",
                description: "Admin panel for online store with inventory management and sales analytics",
                category: "Web Development",
                designer: "Development Team"
              },
              {
                title: "Food Delivery App",
                description: "Mobile app for food ordering with real-time tracking and payment integration",
                category: "Mobile App",
                designer: "UI/UX Team"
              },
              {
                title: "Restaurant Management System",
                description: "Complete POS system with order management and customer tracking",
                category: "Software Solution",
                designer: "Backend Team"
              }
            ].map((project, index) => (
              <ProjectCard
                key={index}
                initial={{ opacity: 0, y: 5 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.15, ease: "easeOut", delay: Math.min(index * 0.03, 0.1) }}
              >
                <ProjectImage>
                  {project.title}
                </ProjectImage>
                <ProjectContent>
                  <ProjectTitle>{project.title}</ProjectTitle>
                  <p style={{ color: '#ffffff', marginBottom: '15px' }}>{project.description}</p>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                    <span style={{ 
                      background: '#e0e7ff', 
                      color: '#3730a3', 
                      padding: '4px 8px', 
                      borderRadius: '6px', 
                      fontSize: '12px' 
                    }}>
                      {project.category}
                    </span>
                  </div>
                  <ProjectMeta>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <User size={16} />
                      {project.designer}
                    </div>
                    <div style={{ display: 'flex', gap: '15px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Eye size={16} />
                        {mockStats.views + (index + 1) * 12}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <Heart size={16} />
                        {mockStats.likes + (index + 1) * 6}
                      </span>
                    </div>
                  </ProjectMeta>
                </ProjectContent>
              </ProjectCard>
            ))
          )}
        </ProjectGrid>
      </Section>

      {/* 7. Certifications & Partnerships */}
      <CertificationsAndPartnerships />

      {/* 8. Trust Signals */}
      <TrustSignals />



      {/* 10. Client Testimonials */}
      <TestimonialSection>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <SectionTitle>Client Success Stories</SectionTitle>
          <SectionSubtitle>
            See how InitX Technologies has transformed businesses and accelerated growth
          </SectionSubtitle>
          
          <TestimonialGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                initial={{ opacity: 0, y: 5 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.15, ease: "easeOut", delay: Math.min(index * 0.04, 0.1) }}
              >
                <div style={{ display: 'flex', marginBottom: '15px' }}>
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#fbbf24" color="#fbbf24" />
                  ))}
                </div>
                <TestimonialText>"{testimonial.text}"</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>
                    {testimonial.author.charAt(0)}
                  </AuthorAvatar>
                  <div>
                    <div style={{ fontWeight: '600', color: '#87ceeb' }}>{testimonial.author}</div>
                    <div style={{ color: '#ffffff', fontSize: '14px' }}>{testimonial.role}</div>
                    <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>{testimonial.company}</div>
                  </div>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialGrid>
        </div>
      </TestimonialSection>

      {/* 11. CTA Banner */}
      <CTABanner />

      {/* 12. FAQ */}
      <FAQ />
    </>
  );
};

export default Home;
